import ChatEventCodes from "./ChatEventCodes";
/**
 * A class to encapsulate the functionality of the websocket functions needed
 * to connect, interact, reconnect, and close chat with the Kore system.
 */
export default class KoreWebSocketManager {

    /**
     * Inititalizes a new instance of @typedef KoreWebSocketManager
     * @param {String} taskBotId The ID of the bot being connected to.
     * @param {String} apiRootUrl The API root for the WSS request.
     * @param {Function} renderChatHistory The parent class function to render chat history before making a connection.
     */
    constructor(taskBotId, apiRootUrl, renderChatHistory) {
        // This is still us assuming we are using our in-house service.
        this.wssApiUrl = `${apiRootUrl}/ddas-chatauthz/1.0/ddas-chatauthorizations/public`;
        /** @type {String} */
        this.url = '';
        /** @type {WebSocket} */
        this.webSocket = null;
        this.botInfo = {
            //'chatBot': chatBot,
            'taskBotId': taskBotId
        };

        /**@type {Boolean} */
        this.isConnected = false;
        /**@type {Boolean} */
        this.isReconnecting = false;
        /** @type {Number} */
        this.pingTimer = 30000;
        /** @type {Number} */
        this.maxPongWait = 5000;
        this.pingIntervalId = undefined;
        this.pongTimer = undefined;
        this.pingTimeoutId = undefined;
        this.maxReconnectAttempts = 10;
        this.reconnectAttempts = 0;
        this.timeoutId = undefined;
        /** @type {Number} */
        this.maxIdleMinutes = 20;
        /** @type {Number} */
        this.maxIdleTime = this.maxIdleMinutes*60*1000;
        // Endpoint does not accept the URI-encoded query strings
        this.pageLocation = `${window.location.origin}${window.location.pathname}`.toLowerCase();
        this.pageLanguage = navigator.language.toLowerCase();
        /** @type {Boolean} */
        this.chatAuthenticated = false;
        /** @type {String} */
        this.chatApplyProduct = '';
        this.renderChatHistory = renderChatHistory;
        this.socketUrlAttempts = 0;
        this.maxSocketUrlAttempts = 1;
        this.blockSocket = false;
        this.authenticated = false;
    }

    /**
     * Builds the URL parameters for the call to our srvice to retreive the WSS URL.
     * Location and language should both come from the user's browser. ContinueChat
     * will set the chat history ask. ChatApplyProduct is set only by OAO currently
     * which can be a comma delimited list. We have for now determined that the first
     * product present is the most crucial and are only passing that single one. This
     * can be modified for future use cases.
     * @param {Boolean} continueChat If chat history should be retrieved or not.
     */
    buildUrlParams(continueChat) {
        const urlParams = new URLSearchParams();
        urlParams.append('botReference', this.botInfo.taskBotId);
        urlParams.append('location', this.pageLocation);
        urlParams.append('language', this.pageLanguage);
        urlParams.append('time', Date.now()); //Add this to bust the browser cache for the GET requests for a persistent user.
        if (continueChat) {
            urlParams.append('historyLimit', '10');
        }
        else {
            urlParams.append('historyLimit', '0');
        }
        this.chatApplyProduct = window.chatApplyProduct || '';
        if (this.chatApplyProduct.trim() !== '') {
            this.chatApplyProduct = window.chatApplyProduct;
            let chatApplyProduct;
            const commaIndex = this.chatApplyProduct.indexOf(','); //This is if OAO sets multiple products on a single page.
            if (commaIndex === -1) {
                chatApplyProduct = this.chatApplyProduct;
            }
            else {
                chatApplyProduct = this.chatApplyProduct.substring(0, commaIndex);
            }
            urlParams.append('applyProduct', chatApplyProduct);
        }

        return urlParams;
    }

    /**
     * Closes the current websocket and alerts the parent element that the connection is closed.
     */
    closeConnection() {
        if (!this.webSocket) {
            this.isConnected = false;
            this.isReconnecting = false;
            return;
        }
        this.isConnected = false;
        this.isReconnecting = false;
        // This probably is 1000 for "close normal" by default, but spec seems to leave some room for ambiguity in browsers
        this.webSocket.close(1000);
        this.webSocket = undefined;
        clearInterval(this.pingIntervalId);
        clearTimeout(this.pingTimeoutId);
        clearTimeout(this.timeoutId);
        document.dispatchEvent(new Event(ChatEventCodes.ChatDisconnected));
        console.log('Connection was closed.');
    }

    /**
     * Create the socket connection and then set the appropriate listeners against the connection.
     * @param {Boolean} continueChat Indicates if the chat is ongoing or if it should start new.
     */
    buildSocket(continueChat) {
        if(this.blockSocket) {
            this.blockSocket = false;
            return;
        }
        const socketUrl = new URL(this.url);
        if (continueChat) {
            socketUrl.searchParams.set('isReconnect', 'true');
        }
        const socket = new WebSocket(socketUrl);
        this.webSocket = socket;

        if (this.webSocket) {
            this.setEventListeners();
        }
    }

    /**
     * Creates the initial socket connection. If already connected ignore the call.
     * @param {Boolean} continueChat If the chat should be continued with chat history.
     */
    createSocketConnection(continueChat = false) {
        if (!this.isConnected) {
            this.blockSocket = false;
            if (window.chatAuthenticated) {
                this.authenticated = true;
                const pageLocation = window.location;
                /**
                 * Initially we used string.startsWith and string.endsWith but ROL has a polyfill that is overrinding
                 * these functions from a Microsoft aspx standpoint. So we needed to back down to an index search
                 * for both functions.
                 */
                const host = pageLocation.host;
                const domain = '.huntington.com';
                if (host.lastIndexOf('onlinebanking', 0) === 0 && host.indexOf(domain, host.length - domain.length) !== -1) {
                    this.getSocketUrl(`${pageLocation.origin}/rol/Retail/ddas-chatauthz/1.0/ddas-chatauthorizations`, continueChat);
                }
                else {
                    this.getSocketUrl('https://onlinebanking.huntington.com/rol/Retail/ddas-chatauthz/1.0/ddas-chatauthorizations', continueChat);
                }
            }
            else {
                this.getSocketUrl(this.wssApiUrl, continueChat);
            }
        }
    }

    /**
     * This sets the flag the is read in buildSocket() to stop the creation of the WS completely.
     * This generally only needs done in the instance where the web request for the socket URL
     * is pending but the user closes the chat window. Without this, the window will "close", but
     * the connection will occur in the background regardless.
     */
    forceBlockSocket() {
        this.blockSocket = true;
    }

    /**
     * Creates a new XMLHttpRequest and sends to get the WSS URL and the chat history (if applicable).
     * If chat history is returned, then render the chat history and create the socket connection. If not,
     * only create the socket connection.
     * @param {String} url The endpoint to request the WSS URL and chat history.
     * @param {Boolean} continueChat Flag indicating if the chat is being continued or to start fresh.
     */
    getSocketUrl(url, continueChat) {
        const request = new XMLHttpRequest();
        request.open('GET', `${url}?${this.buildUrlParams(continueChat)}`);
        request.withCredentials = true;
        request.responseType = 'json';
        request.onload = () => {
            if (request.status >= 400 || !request.response) {
                if (this.socketUrlAttempts < this.maxSocketUrlAttempts) {
                    if (this.authenticated) {
                        console.error('Error trying to connect to the auth service. Re-attempting with the unathenticated service.');
                        this.authenticated = false;
                    }
                    this.socketUrlAttempts++;
                    this.getSocketUrl(this.wssApiUrl, continueChat);
                    return;
                }
                else {
                    document.dispatchEvent(new Event(ChatEventCodes.WebsocketServiceUnavailable));
                    console.error(`Error getting websocket URL: Status:${request.status}, Reason:${request.statusText}`);
                    return;
                }
            }
            const chatAuthorization = request.response['payload']['ddas-chatauthorizations'][0];
            this.url = chatAuthorization.websocketUrl;
            const chatHistory = chatAuthorization.chatHistory;

            if (chatHistory?.messages && chatHistory.messages.length > 0) {
                this.renderChatHistory(chatHistory.messages);
                this.buildSocket(continueChat);
            }
            else {
                // Set to false since there is possibly an existing cookie but no history length
                // to display the welcome message again and start over.
                this.buildSocket(false);
            }
        };
        request.onerror = () => {
            if (this.socketUrlAttempts < this.maxSocketUrlAttempts) {
                if (this.authenticated) {
                    this.authenticated = false;
                    console.error('Error trying to connect to the auth service. Re-attempting with the unathenticated service.');
                }
                this.socketUrlAttempts++;
                this.getSocketUrl(this.wssApiUrl, continueChat);
            }
            else {
                document.dispatchEvent(new Event(ChatEventCodes.WebsocketServiceUnavailable));
            }
        };
        request.send();
    }

    /**
     * Clears the current timeout based on the last received message and
     * sets a new timeout based on the maxIdleTime. If the timeout is hit,
     * the connection will be closed and an event will be sent to close the
     * chat window.
     */
    handleSetAndClearTimeouts() {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            clearTimeout(this.pingTimeoutId);
            this.closeConnection();
            document.dispatchEvent(new Event(ChatEventCodes.ChatTimeout));
        }, this.maxIdleTime);
    }

    /**
     * Sends a ping to the websocket server. If a pong is not received within the defined
     * maximum wait time, then the socket will either: reconnect if we have not made max connection
     * attempts; close the socket connection.
     */
    ping() {
        this.webSocket.send(JSON.stringify({
            'type': 'ping',
            'resourceid': './bot-message',
            'botInfo': this.botInfo,
            'id': Date.now()
        }));
        this.pingTimeoutId = setTimeout(() => {
            clearInterval(this.pingIntervalId);
            this.closeConnection();
        }, this.maxPongWait);
    }

    /**
     * Clears the timout set in the ping() to prevent reconnection or closure of the socket connection.
     */
    pong() {
        clearTimeout(this.pingTimeoutId);
    }

    /**
     * Send for data through as a message to the bot.
     * @param {Object} formData The collection of input names/values from the form being submitted.
     */
    sendFormSubmission(formData) {
        if (this.isConnected) {
            this.webSocket.send(JSON.stringify({
                'message': {
                    'messageObject': formData
                },
                'resourceid': '/bot.message',
                'botInfo': this.botInfo
            }));
            this.handleSetAndClearTimeouts();
        }
    }

    /**
     * If connected, send the typing ended message.
     */
    sendTypingEnded() {
        if (this.isConnected) {
            this.webSocket.send(JSON.stringify({
                'event': 'stop_typing',
                'message': {
                    'body': '',
                    'type': 'TYPING_STOP'
                },
                'preDefinedEvent': {
                    'type': 'TYPING_STOP'
                },
                'resourceid': '/bot.message',
                'botInfo': this.botInfo
            }));
        }
    }

    /**
     * If connected, send the typing started message.
     */
    sendTypingStarted() {
        if (this.isConnected) {
            this.webSocket.send(JSON.stringify({
                'event': 'typing',
                'message': {
                    'body': '',
                    'type': ''
                },
                'preDefinedEvent': {
                    'type': 'TYPING_STARTED'
                },
                'resourceid': '/bot.message',
                'botInfo': this.botInfo
            }));
        }
    }

    /**
     * Sends the user's input either through an interactive bot template or what the
     * user has typed and sent.
     * @param {String} inputValue The user's typed or clicked value to send to the bot.
     */
    sendUserInput(inputValue) {
        if (this.isConnected) {
            this.webSocket.send(JSON.stringify({
                'message': {
                    'body': inputValue
                },
                'resourceid': '/bot.message',
                'botInfo': this.botInfo
            }));
            this.handleSetAndClearTimeouts();
        }
    }

    /**
     * Sends the user merge and continue event to the Kore bot after connection.
     * This should be sent after the initial "Bot_Active" message is received through
     * the websocket connection.
     */
    sendUserMergeContinue() {
        if (this.isConnected) {
            this.webSocket.send(JSON.stringify({
                'clientMessageId': Date.now(),
                'customEvent': 'USER_MERGE',
                'resourceid': '/bot.clientEvent',
                'botInfo': this.botInfo
            }));
            this.handleSetAndClearTimeouts();
        }
    }

    /**
     * Sets the event listeners against the websocket object.
     */
    setEventListeners() {
        this.webSocket.addEventListener('open', (e) => {
            if (this.isReconnecting) {
                this.isReconnecting = false;
            }
            this.isConnected = true;
            // Send the login flow ended message. The parent contorller will determine if the cookie still exists
            // and send the send request for the merge event to the WS.
            document.dispatchEvent(new Event(ChatEventCodes.ChatConnected));

            //Set an interval to call continuously against the defined ping timer. ping() will handle
            // what happens if a pong is not received in a defined period of time.
            this.pingIntervalId = setInterval(() => this.ping(), this.pingTimer);
            this.handleSetAndClearTimeouts();
        });

        this.webSocket.addEventListener('message', (e) => {
            if (e.origin !== 'wss://rtm.kore.ai') {
                console.error('Received message from invalid WS origin.');
                return;
            }
            this.socketRecievedMessage(e);
        });

        this.webSocket.addEventListener('error', (e) => {
            // Need to add a standalone error handling function. Will probably need to bubble
            // connection state in that as well so the parent component can handle UI display
            // for connection status.
            this.socketRecievedError(e);
        });

        this.webSocket.addEventListener('close', (e) => {
            // Seemingly only Firefox triggers "going away" websocket close events on close tab / navigate
            // These should be ignored so we maintain the chat widget state.
            if (e.code == 1001) {
              return;
            }

            this.isConnected = false;
            this.isReconnecting = false;
            this.blockSocket = false;
            // Bubble the event up to the parent element so it can also maintain connection state.
            document.dispatchEvent(new Event(ChatEventCodes.ChatDisconnected));
        });
    }

    /**
     * For now log the error in the console and dispatch the error event up to the main chat handler.
     * @param {Event} e The error event object.
     */
    socketRecievedError(e) {
        console.error(e);
        document.dispatchEvent(new Event(ChatEventCodes.ChatDisconnected));
        document.dispatchEvent(new Event(ChatEventCodes.WebsocketError));
    }

    /**
     * Extracts the data from the message event, determines the type, and follows the appropriate action
     * for the type of message.
     * @param {Event} e The message received event.
     */
    socketRecievedMessage(e) {
        const data = JSON.parse(e.data);
        console.log(data);
        switch (data.type) {
            case 'Agent_Session_End':
                document.dispatchEvent(new Event(ChatEventCodes.AgentSessionEnd));
                break;
            case 'Agent_Session_Start':
                document.dispatchEvent(new Event(ChatEventCodes.AgentSessionStart));
                break;
            case 'Bot_Active': {
                const chatConnectedData = {
                    loginFlowSuccess: this.authenticated
                };
                document.dispatchEvent(new MessageEvent(ChatEventCodes.BotActive, {data: chatConnectedData}));
                break;
            }
            case 'bot_response':
                document.dispatchEvent(new MessageEvent(ChatEventCodes.BotResponse , {bubbles: true, data: data}));
                break;
            case 'botKitUnreachable':
                document.dispatchEvent(new MessageEvent(ChatEventCodes.BotKitUnreachable, {bubbles: true, data: data}));
                break;
            case 'pong':
                this.pong();
                break;
            default:
                console.log(e);
        }
    }
}